import { formatToDate } from '@/helpers/dateHelper';
import { useAppDispatch } from '@/state/hooks';
import { TradesResponse } from '@/types/sso';
import { useCallback, useEffect, useState } from 'react';
import { fetchPortalAPI, useSso } from '../providers/SSOProvider';

export interface UseTradesFilterProps {
    startDate: { label: string; value: Date };
    endDate: { label: string; value: Date };
}

export interface UseTradesFilterReturn {
    data: TradesResponse[];
    isLoading: boolean;
    isValidating: boolean;
    mutate: () => void;
    error: Error | null;
    isResultsFetched: boolean;
    setIsResultsFetched: (value: boolean) => void;
}

export const useTradesFilter = (props: UseTradesFilterProps): UseTradesFilterReturn => {
    const { startDate, endDate } = props;
    const dispatch = useAppDispatch();

    const { jwt, foundEntity, onFetchJWT } = useSso();

    const [tradeBookings, setTradeBookings] = useState<TradesResponse[]>([]);
    const [isResultsFetched, setIsResultsFetched] = useState<boolean>(false);
    const [isTradeBookingsLoading, setIsTradeBookingsLoading] = useState<boolean>(false);
    const [isTradeBookingsValidating, setIsTradeBookingsValidating] = useState<boolean>(false);
    const [tradeBookingsError, setTradeBookingsError] = useState<Error | null>(null);

    const handleApiError = useCallback(
        (error) => {
            setIsTradeBookingsLoading(false);
            setIsTradeBookingsValidating(false);
        },
        [setIsTradeBookingsLoading, setIsTradeBookingsLoading]
    );

    const refreshAllTradeBookings = useCallback(async () => {
        setIsTradeBookingsLoading(true);
        setIsTradeBookingsValidating(true);
        setIsResultsFetched(true);

        try {
            if (foundEntity) {
                const trades = await fetchPortalAPI<TradesResponse[]>(
                    onFetchJWT,
                    '/api/trades/list',
                    jwt,
                    {
                        id: foundEntity.entityId.toString(),
                        fromTradeDate: formatToDate(startDate.value),
                        toTradeDate: formatToDate(endDate.value)
                    },
                    'POST'
                );
                setIsTradeBookingsLoading(false);
                setIsTradeBookingsValidating(false);
                setIsResultsFetched(true);
                setTradeBookings(trades || []);
            }
        } catch (error: any) {
            setTradeBookingsError(error);
            handleApiError({
                title: error?.name || 'Unknown Error',
                message: error?.message || 'An unknown error occurred'
            });
        }
    }, [
        startDate,
        endDate,
        foundEntity,
        isResultsFetched,
        dispatch,
        handleApiError,
        setIsTradeBookingsLoading,
        setIsTradeBookingsValidating,
        setTradeBookings
    ]);

    useEffect(() => {
        setTradeBookings([]);
        setIsResultsFetched(false);
    }, [foundEntity]);

    return {
        data: tradeBookings,
        isLoading: isTradeBookingsLoading,
        isValidating: isTradeBookingsValidating,
        mutate: refreshAllTradeBookings,
        error: tradeBookingsError,
        isResultsFetched,
        setIsResultsFetched
    };
};
